import { useQuery } from "@tanstack/react-query";
import { api, ApiError, type BlockInstance, type PageSlug } from "@/lib/api";

/**
 * Loads the block list for a single page. The legacy `/api/site/content`
 * response is still fetched separately by SiteLayout for the global Contact
 * + AgeGate copy and the legacy image map.
 *
 * 404 errors (unknown / hidden page) propagate so DynamicPage can show a
 * NotFound view instead of an empty skeleton.
 */
export function useSitePage(slug: PageSlug) {
  return useQuery({
    queryKey: ["site-page", slug],
    queryFn: () => api.getSitePage(slug),
    staleTime: 5_000,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });
}

/** Loads the public list of pages — used by SiteLayout for nav links. */
export function useSitePages() {
  return useQuery({
    queryKey: ["site-pages"],
    queryFn: api.listSitePages,
    staleTime: 5_000,
  });
}

/** Filters out hidden instances and yields what should render publicly. */
export function visibleBlocks(blocks: BlockInstance[]): BlockInstance[] {
  return blocks.filter((b) => b.visible !== false);
}
